import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, MessageCircle, Send, X } from "lucide-react";
import { scrollToId } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "How do I rank higher in my area?",
  "SEO or Google Ads — which first?",
  "What is remarketing, in plain words?",
];

export default function AskTodd() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(
    `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const goBook = () => {
    setOpen(false);
    setTimeout(() => scrollToId("book"), 320);
  };

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages([
      ...messages,
      { role: "user", text: msg },
      { role: "assistant", text: "" },
    ]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionRef.current }),
      });
      if (!res.ok) throw new Error("bad status");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done: d, value } = await reader.read();
        if (d) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data:")) continue;
          const payload = JSON.parse(p.slice(5).trim());
          setMessages((m) => {
            const c = [...m];
            if (payload.d) c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text + payload.d };
            if (payload.error) c[c.length - 1] = { role: "assistant", text: payload.error };
            return c;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", text: "Connection hiccup — please try that again." };
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle-button"
        aria-label="Toggle Ask Todd chat"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-[60] flex h-14 w-14 items-center justify-center rounded-2xl bg-flame text-obsidian shadow-[0_0_32px_rgba(255,85,0,0.45)] transition-transform duration-300 hover:scale-105"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="glass fixed bottom-24 right-6 z-[60] flex h-[480px] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-slate-200"
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <div className="font-display text-xl font-bold text-slate-900">
                  Ask Todd
                </div>
                <div className="font-mono text-[13px] uppercase tracking-[0.2em] text-flame">
                  AI assistant · answers instantly
                </div>
              </div>
              <button
                data-testid="chat-handoff-header-button"
                onClick={goBook}
                className="rounded-2xl bg-flame px-4 py-2 font-mono text-[13px] font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_24px_rgba(255,85,0,0.45)]"
              >
                Book a free call
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
              {messages.length === 0 && (
                <div>
                  <p className="text-base leading-relaxed text-mist">
                    Ask me anything about SEO, ads or your website — plain
                    answers, no jargon.
                  </p>
                  <div className="mt-4 space-y-2">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        data-testid="chat-suggestion"
                        onClick={() => send(s)}
                        className="w-full rounded-2xl border border-slate-200 bg-white/[0.05] px-4 py-3 text-left text-base text-slate-800 transition-colors hover:border-flame/50 hover:text-flame"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${i}`}
                  className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
                >
                  <div
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-base leading-relaxed ${
                      m.role === "user"
                        ? "bg-flame text-obsidian"
                        : "border border-slate-200 bg-white/[0.06] text-slate-700"
                    }`}
                  >
                    {m.text ||
                      (streaming && i === messages.length - 1 ? (
                        <Loader2 className="h-4 w-4 animate-spin text-flame" />
                      ) : (
                        m.text
                      ))}
                  </div>
                </div>
              ))}
              {messages.length > 0 && !streaming && (
                <button
                  data-testid="chat-handoff-button"
                  onClick={goBook}
                  className="w-full rounded-2xl border border-flame/40 bg-flame/10 px-4 py-3 text-base font-semibold text-flame transition-colors hover:bg-flame/20"
                >
                  Ready when you are — book your free call
                </button>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-slate-200 p-3"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question…"
                className="flex-1 rounded-full border border-slate-300 bg-slate-50 px-4 py-2.5 text-base text-slate-900 placeholder:text-slate-700 outline-none transition-colors focus:border-flame"
              />
              <button
                data-testid="chat-send-button"
                type="submit"
                disabled={streaming || !input.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-flame text-obsidian transition-opacity disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
